import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageTransition } from '../context/TransitionContext.jsx';
import { getPage, links } from '../config/navigation.js';
import { siteName, tagline } from '../config/site.js';
import Logo from './Logo.jsx';
import CalloutCard from './CalloutCard.jsx';
import '../assets/css/ext/hamburgers.css';
import '../assets/css/src/header.css';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { navigateTo } = usePageTransition();
  const location = useLocation();
  const navRef = useRef(null);

  // Close menu when viewport widens past 768px
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth > 768) {
        setIsOpen(false);
      }
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  function toggleMenu() {
    const nav = navRef.current;
    if (nav) {
      nav.classList.add('nav-animate');
      setTimeout(() => nav.classList.remove('nav-animate'), 350);
    }
    setIsOpen((prev) => !prev);
    setOpenDropdown(null);
  }

  function handleClick(e, to) {
    e.preventDefault();
    setIsOpen(false);
    setOpenDropdown(null);
    document.activeElement?.blur();
    if (location.pathname === to) return;
    navigateTo(to);
  }

  function toggleDropdown(e, to) {
    e.preventDefault();
    setOpenDropdown((prev) => (prev === to ? null : to));
  }

  const { cover, title, bgTarget, bodyBg, callout, showTitle } = getPage(location.pathname);

  useEffect(() => {
    if (bodyBg) {
      document.body.style.setProperty('--page-bg-image', `url(${bodyBg})`);
      document.body.classList.add('has-body-bg');
    } else {
      document.body.style.removeProperty('--page-bg-image');
      document.body.classList.remove('has-body-bg');
    }
    return () => {
      document.body.style.removeProperty('--page-bg-image');
      document.body.classList.remove('has-body-bg');
    };
  }, [bodyBg]);

  useEffect(() => {
    if (callout) {
      document.body.classList.add('has-callout');
    } else {
      document.body.classList.remove('has-callout');
    }
    return () => {
      document.body.classList.remove('has-callout');
    };
  }, [callout]);

  return (
    <header id="header">
      <nav id="navrow1" ref={navRef}>
        <a className="site-name" href="/" onClick={(e) => handleClick(e, '/')}>{siteName}</a>
        <button
          className={`hamburger hamburger--squeeze${isOpen ? ' is-active' : ''}`}
          type="button"
          aria-label="Toggle navigation"
          aria-expanded={isOpen}
          onClick={toggleMenu}
        >
          <span className="hamburger-box">
            <span className="hamburger-inner" />
          </span>
        </button>

        <ul id="nav-links" className={isOpen ? 'nav-open' : ''}>
          {links.map(({ to, label, end, children }) => {
            const isActive = end
              ? location.pathname === to
              : location.pathname.startsWith(to);
            const hasDropdown = children && children.length > 0;
            const isDropdownOpen = openDropdown === to;
            return (
              <li key={to} className={[
                hasDropdown ? 'has-dropdown' : '',
                isDropdownOpen ? 'dropdown-open' : '',
              ].filter(Boolean).join(' ')}>
                <a
                  href={to}
                  className={isActive ? 'active' : ''}
                  onClick={(e) => handleClick(e, to)}
                >
                  {label}
                </a>
                {hasDropdown && (
                  <button
                    className="dropdown-toggle"
                    aria-label={`Toggle ${label} submenu`}
                    aria-expanded={isDropdownOpen}
                    onClick={(e) => toggleDropdown(e, to)}
                  >
                    <span className="dropdown-arrow" aria-hidden="true" />
                  </button>
                )}
                {hasDropdown && (
                  <ul className="nav-dropdown">
                    {children.map((child) => (
                      <li key={child.to}>
                        <a
                          href={child.to}
                          className={location.pathname === child.to ? 'active' : ''}
                          onClick={(e) => handleClick(e, child.to)}
                        >
                          {child.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      <div
        id="cover-container"
        className={bodyBg ? 'has-second-image' : undefined}
        style={bgTarget === 'cover-container' ? { '--container-bg-image': `url(${cover})` } : undefined}
      >
        {bgTarget === 'cover' && (
          <div
            id="cover"
            style={{ '--cover-image': `url(${cover})` }}
            role="img"
            aria-label={tagline}
          />
        )}
        {callout ? (
          <CalloutCard {...callout} />
        ) : (
          showTitle !== false && <h1>{title}</h1>
        )}
      </div>
      <div id="header-logo-row">
        <div id="header-logo">
          <Logo />
        </div>
      </div>
    </header>
  );
}
